import pptxgen from "pptxgenjs";
import * as htmlToImage from "html-to-image";
import { downloadAsPDF } from "@/lib/pdf";

/**
 * Flatten a training course JSON into a compact markdown-like block
 * for the PDF exporter.
 */
export function courseToMarkdown(course) {
  if (!course) return "";
  const L = [];
  L.push(`# ${course.course_title || "Training Course"}`);
  if (course.learning_objectives?.length) {
    L.push(`## Learning Objectives`);
    course.learning_objectives.forEach((o, i) => L.push(`${i + 1}. ${o}`));
  }
  (course.modules || []).forEach((m, i) => {
    L.push(`## ${m.name || `Module ${i + 1}`} — ${m.duration_minutes || 0} min`);
    if (m.topics?.length) {
      L.push(`### Topics`);
      m.topics.forEach((t) => L.push(`- ${t}`));
    }
    if (m.case_study) {
      L.push(`### Case Study`);
      L.push(m.case_study);
    }
    if (m.practical_exercise) {
      L.push(`### Practical Exercise`);
      L.push(m.practical_exercise);
    }
    if (m.knowledge_check?.q) {
      const kc = m.knowledge_check;
      L.push(`### Knowledge Check`);
      L.push(`**Q:** ${kc.q}`);
      (kc.options || []).forEach((o, k) => {
        const marker = k === kc.answer_index ? "**(correct)**" : "";
        L.push(`- ${String.fromCharCode(65 + k)}. ${o} ${marker}`);
      });
    }
  });
  if (course.certification) {
    L.push(`## Certification`);
    L.push(`${course.certification.name || ""} — ${course.certification.level || ""} · ${course.certification.credits || 0} CPD`);
  }
  return L.join("\n\n");
}

export function exportCoursePDF(course) {
  downloadAsPDF({
    title: course?.course_title || "Training Course",
    body: courseToMarkdown(course),
    kind: "training",
  });
}

/**
 * Build a branded .pptx from the training course.
 * Google Slides opens .pptx natively — same file works for both.
 */
export function exportCoursePPT(course, { googleSlides = false } = {}) {
  const pres = new pptxgen();
  pres.layout = "LAYOUT_WIDE"; // 13.3 x 7.5"
  pres.title = course?.course_title || "3C AI Training";
  pres.company = "3C AI — Engineering Intelligence Platform";

  const NAVY = "020617";
  const NAVY_SOFT = "0F172A";
  const CYAN = "06B6D4";
  const SLATE = "94A3B8";
  const WHITE = "F8FAFC";
  const TEXT = "E2E8F0";

  const addFooter = (slide) => {
    slide.background = { color: NAVY };
    slide.addText("3C AI — Contract Intelligence Platform", {
      x: 0.4, y: 7.1, w: 8, h: 0.3,
      fontSize: 9, color: SLATE, fontFace: "Calibri",
    });
    slide.addShape(pres.ShapeType.rect, {
      x: 12.6, y: 0.3, w: 0.35, h: 0.35, fill: { color: CYAN }, line: { color: CYAN },
    });
    slide.addText("3C AI", {
      x: 11.4, y: 0.3, w: 1.1, h: 0.35, fontSize: 12, bold: true, color: WHITE, align: "right",
    });
  };

  // Cover
  const cover = pres.addSlide();
  addFooter(cover);
  cover.addText(course?.course_title || "3C AI Training Course", {
    x: 0.6, y: 2.6, w: 12, h: 1.5, fontSize: 40, bold: true, color: WHITE, fontFace: "Calibri",
  });
  cover.addShape(pres.ShapeType.rect, { x: 0.6, y: 4.15, w: 1, h: 0.06, fill: { color: CYAN }, line: { color: CYAN } });
  cover.addText(
    `${(course?.modules || []).length} modules · Certification: ${course?.certification?.level || "Foundation"}`,
    { x: 0.6, y: 4.35, w: 10, h: 0.4, fontSize: 14, color: SLATE, fontFace: "Calibri" }
  );

  // Objectives
  const obj = pres.addSlide();
  addFooter(obj);
  obj.addText("Learning Objectives", {
    x: 0.6, y: 0.6, w: 12, h: 0.6, fontSize: 28, bold: true, color: WHITE,
  });
  obj.addShape(pres.ShapeType.rect, { x: 0.6, y: 1.2, w: 0.8, h: 0.05, fill: { color: CYAN }, line: { color: CYAN } });
  (course?.learning_objectives || []).forEach((o, i) => {
    obj.addText(`${i + 1}.  ${o}`, {
      x: 0.6, y: 1.5 + i * 0.55, w: 12, h: 0.5, fontSize: 14, color: TEXT, fontFace: "Calibri",
    });
  });

  // One slide per module
  (course?.modules || []).forEach((m, mi) => {
    const s = pres.addSlide();
    addFooter(s);
    s.addText(m.name || `Module ${mi + 1}`, {
      x: 0.6, y: 0.5, w: 10, h: 0.6, fontSize: 24, bold: true, color: WHITE,
    });
    s.addText(`${m.duration_minutes || 0} min`, {
      x: 10.7, y: 0.55, w: 1.9, h: 0.5, fontSize: 12, color: CYAN, align: "right",
    });
    s.addShape(pres.ShapeType.rect, { x: 0.6, y: 1.1, w: 0.8, h: 0.05, fill: { color: CYAN }, line: { color: CYAN } });

    // Topics
    s.addText("Topics", { x: 0.6, y: 1.3, w: 4, h: 0.35, fontSize: 12, bold: true, color: CYAN });
    (m.topics || []).forEach((t, ti) => {
      s.addText(`▸  ${t}`, { x: 0.6, y: 1.7 + ti * 0.35, w: 5.5, h: 0.35, fontSize: 12, color: TEXT });
    });

    // Case study
    s.addText("Case Study", { x: 6.6, y: 1.3, w: 6, h: 0.35, fontSize: 12, bold: true, color: CYAN });
    s.addText(m.case_study || "—", {
      x: 6.6, y: 1.7, w: 6.2, h: 1.7, fontSize: 11, color: TEXT, valign: "top",
    });

    // Practical exercise
    s.addText("Practical Exercise", { x: 6.6, y: 3.5, w: 6, h: 0.35, fontSize: 12, bold: true, color: CYAN });
    s.addText(m.practical_exercise || "—", {
      x: 6.6, y: 3.9, w: 6.2, h: 1.7, fontSize: 11, color: TEXT, valign: "top",
    });

    // Knowledge check
    if (m.knowledge_check?.q) {
      s.addText("Knowledge Check", { x: 0.6, y: 3.5, w: 6, h: 0.35, fontSize: 12, bold: true, color: CYAN });
      s.addText(m.knowledge_check.q, {
        x: 0.6, y: 3.9, w: 5.7, h: 0.9, fontSize: 11, color: TEXT, valign: "top",
      });
      (m.knowledge_check.options || []).slice(0, 4).forEach((o, oi) => {
        const isCorrect = oi === m.knowledge_check.answer_index;
        s.addText(`${String.fromCharCode(65 + oi)}.  ${o}${isCorrect ? "  ✓" : ""}`, {
          x: 0.6, y: 4.8 + oi * 0.32, w: 5.7, h: 0.32, fontSize: 10,
          color: isCorrect ? "10B981" : SLATE,
        });
      });
    }
  });

  // Certification
  if (course?.certification) {
    const c = pres.addSlide();
    addFooter(c);
    c.addText("Certification", { x: 0.6, y: 2.2, w: 12, h: 0.6, fontSize: 28, bold: true, color: WHITE });
    c.addShape(pres.ShapeType.rect, { x: 0.6, y: 2.85, w: 0.8, h: 0.05, fill: { color: CYAN }, line: { color: CYAN } });
    c.addText(course.certification.name || "3C AI Certification", {
      x: 0.6, y: 3.2, w: 12, h: 0.7, fontSize: 22, color: WHITE, bold: true,
    });
    c.addText(
      `Level: ${course.certification.level || "Foundation"}   ·   CPD credits: ${course.certification.credits || 0}`,
      { x: 0.6, y: 4.0, w: 12, h: 0.5, fontSize: 14, color: CYAN }
    );
    c.addText(
      "Issued upon completion of all four modules and knowledge checks.",
      { x: 0.6, y: 4.6, w: 12, h: 0.5, fontSize: 12, color: SLATE, italic: true }
    );
  }

  const safe = (course?.course_title || "training").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  pres.writeFile({ fileName: `${safe}.pptx` });

  if (googleSlides) {
    setTimeout(() => {
      window.open("https://slides.google.com/", "_blank", "noopener");
    }, 400);
  }
}

/**
 * Snapshot the visible training results panel as a single PNG.
 */
export async function exportCoursePNG(node, course) {
  if (!node) return;
  const dataUrl = await htmlToImage.toPng(node, {
    backgroundColor: "#020617",
    pixelRatio: 2,
    cacheBust: true,
  });
  const a = document.createElement("a");
  const safe = (course?.course_title || "training").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  a.download = `${safe}.png`;
  a.href = dataUrl;
  a.click();
}
